/**
 * Phase 14.5 — QorBOM Docs · Right-rail in-page TOC.
 *
 * Walks the article content for `h2[id]` + `h3[id]` after mount and
 * builds a sticky, click-to-jump TOC. Highlights the current section
 * using IntersectionObserver so scrolling feels alive.
 *
 * Stripe-style: minimal, no clutter, just anchor text + a single
 * "On this page" header. The right rail hides on <lg screens.
 */
import React, { useEffect, useState } from "react";


export default function RightRailToc({ contentRef, testid }) {
  const [items, setItems]   = useState([]);
  const [activeId, setActive] = useState(null);

  // Build TOC on every mount + content swap.
  useEffect(() => {
    if (!contentRef?.current) return undefined;
    // Tiny delay so the content has rendered + got its IDs from
    // MarkdownRenderer / static JSX.
    const t = window.setTimeout(() => {
      const root = contentRef.current;
      if (!root) return;
      const nodes = root.querySelectorAll("h2[id], h3[id]");
      const parsed = [];
      nodes.forEach((n) => {
        if (!n.id) return;
        parsed.push({
          id:    n.id,
          text:  (n.textContent || "").trim(),
          level: n.tagName === "H3" ? 3 : 2,
        });
      });
      setItems(parsed);
    }, 60);
    return () => window.clearTimeout(t);
    // Re-run when the actual DOM node reference changes (route swap).
  }, [contentRef]);

  // Active-section highlight via IntersectionObserver.
  useEffect(() => {
    if (!items.length) return undefined;
    const elems = items
      .map((i) => document.getElementById(i.id))
      .filter(Boolean);
    if (!elems.length) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        // Pick the topmost entry that's intersecting.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-100px 0px -70% 0px", threshold: 0 },
    );
    elems.forEach((e) => obs.observe(e));
    return () => obs.disconnect();
  }, [items]);

  if (!items.length) return null;

  return (
    <aside
      className="hidden lg:block sticky top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto pr-2"
      data-testid={testid || "qorbom-docs-rightrail"}
      aria-label="On this page"
    >
      <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
        On this page
      </div>
      <ul className="space-y-1 border-l border-slate-200 pl-3 text-[13px]">
        {items.map((it) => (
          <li key={it.id} className={it.level === 3 ? "pl-3" : ""}>
            <a
              href={`#${it.id}`}
              data-testid={`qorbom-docs-rightrail-link-${it.id}`}
              className={
                "block py-0.5 transition-colors " +
                (activeId === it.id
                  ? "font-semibold text-blue-700"
                  : "text-slate-500 hover:text-slate-900")
              }
              onClick={(e) => {
                // Manage the URL fragment ourselves so the back button
                // works + we get scroll-margin behaviour.
                e.preventDefault();
                const el = document.getElementById(it.id);
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  window.history.replaceState(null, "", `#${it.id}`);
                }
              }}
            >
              {it.text}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
}
